export type HistoryFaceIdentity = {
  editorSessionKey: string;
  faceId: number;
  faceNumber: number;
  type: string;
};

export type HistorySnapshot = Record<string, unknown>;

export type FaceReadiness = 'loading' | 'recovery' | 'ready' | 'fatal';

export type CardHistoryState = {
  canUndo: boolean;
  canRedo: boolean;
  isRestoring: boolean;
  lastRestoreError: Error | null;
};

type HistoryEntry = {
  id: number;
  face: HistoryFaceIdentity;
  before: HistorySnapshot;
  after: HistorySnapshot;
  serializedBytes: number;
};

export type FaceSnapshotRestorer = (snapshot: HistorySnapshot) => Promise<void>;

export type PendingHistoryTransaction = {
  hasPendingChanges: () => boolean;
  getBeforeSnapshot: () => HistorySnapshot | null;
  rebase: (snapshot: HistorySnapshot) => void;
  isComposing?: () => boolean;
};

type PendingRegistration = {
  face: HistoryFaceIdentity;
  canvas: object;
  participant: PendingHistoryTransaction;
};

type HistoryCoordinatorDependencies = {
  captureSnapshot: (canvas: object) => HistorySnapshot;
  getCanvas: (face: HistoryFaceIdentity) => object | undefined;
  restoreSnapshot: (face: HistoryFaceIdentity, snapshot: HistorySnapshot) => Promise<void>;
  navigateToFace: (face: HistoryFaceIdentity) => Promise<void>;
  isFaceReady: (face: HistoryFaceIdentity) => boolean;
  setFaceReadiness: (face: HistoryFaceIdentity, readiness: FaceReadiness) => void;
  markFaceFatal: (face: HistoryFaceIdentity, error: Error) => void;
  maxEntries?: number;
  maxSerializedBytes?: number;
};

const DEFAULT_MAX_ENTRIES = 100;
const DEFAULT_MAX_SERIALIZED_BYTES = 20 * 1024 * 1024;

const cloneSnapshot = (snapshot: HistorySnapshot): HistorySnapshot =>
  JSON.parse(JSON.stringify(snapshot)) as HistorySnapshot;

const snapshotsEqual = (first: HistorySnapshot, second: HistorySnapshot): boolean =>
  JSON.stringify(first) === JSON.stringify(second);

const getSerializedByteLength = (value: unknown): number =>
  Array.from(JSON.stringify(value)).reduce((total, character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    if (codePoint <= 0x7f) return total + 1;
    if (codePoint <= 0x7ff) return total + 2;
    if (codePoint <= 0xffff) return total + 3;
    return total + 4;
  }, 0);

export class CardHistoryCoordinator {
  private readonly dependencies: HistoryCoordinatorDependencies;

  private readonly maxEntries: number;

  private readonly maxSerializedBytes: number;

  private undoStack: HistoryEntry[] = [];

  private redoStack: HistoryEntry[] = [];

  private listeners = new Set<() => void>();

  private faceRestorers = new Map<string, FaceSnapshotRestorer>();
  private pendingTransactions = new Map<symbol, PendingRegistration>();
  private isPreparing = false;

  private nextEntryId = 0;

  private isRestoring = false;

  private disposed = false;

  private lastRestoreError: Error | null = null;

  public constructor(dependencies: HistoryCoordinatorDependencies) {
    this.dependencies = dependencies;
    this.maxEntries = dependencies.maxEntries ?? DEFAULT_MAX_ENTRIES;
    this.maxSerializedBytes = dependencies.maxSerializedBytes ?? DEFAULT_MAX_SERIALIZED_BYTES;
  }

  public getState = (): CardHistoryState => ({
    canUndo:
      !this.disposed &&
      !this.isComposing() &&
      (this.hasPendingChanges() || this.isEntryUsable(this.undoStack[this.undoStack.length - 1])),
    canRedo:
      !this.disposed &&
      !this.isComposing() &&
      !this.hasPendingChanges() &&
      this.isEntryUsable(this.redoStack[this.redoStack.length - 1]),
    isRestoring: this.isRestoring,
    lastRestoreError: this.lastRestoreError,
  });

  public subscribe = (listener: () => void): (() => void) => {
    if (this.disposed) {
      return () => undefined;
    }

    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  public refresh = (): void => {
    this.emitChange();
  };

  public registerPendingTransaction = (
    face: HistoryFaceIdentity,
    canvas: object,
    participant: PendingHistoryTransaction,
  ): (() => void) => {
    this.assertActive();
    const token = Symbol();
    this.pendingTransactions.set(token, { face, canvas, participant });
    this.emitChange();
    return () => {
      if (this.pendingTransactions.delete(token)) {
        this.emitChange();
      }
    };
  };

  public registerFaceRestorer = (face: HistoryFaceIdentity, restorer: FaceSnapshotRestorer): (() => void) => {
    const identityKey = this.getIdentityKey(face);
    this.faceRestorers.set(identityKey, restorer);
    return () => {
      if (this.faceRestorers.get(identityKey) === restorer) {
        this.faceRestorers.delete(identityKey);
      }
    };
  };

  public runTransaction = async (
    face: HistoryFaceIdentity,
    canvas: object,
    mutation: () => void | Promise<void>,
    beforeSnapshot?: HistorySnapshot,
  ): Promise<void> => {
    this.assertActive();
    if (this.isRestoring || this.isPreparing || this.isComposing()) {
      throw new Error('Cannot edit while canvas history is restoring');
    }
    if (!this.dependencies.isFaceReady(face)) {
      throw new Error('Cannot edit a card face that is not ready');
    }

    this.flushPendingTransactions(beforeSnapshot ? { canvas, snapshot: beforeSnapshot } : undefined);

    const before = cloneSnapshot(beforeSnapshot ?? this.dependencies.captureSnapshot(canvas));
    try {
      await mutation();
    } catch (mutationError) {
      await this.rollbackTransaction(face, before, mutationError);
      throw mutationError;
    }

    let after: HistorySnapshot;
    try {
      after = cloneSnapshot(this.dependencies.captureSnapshot(canvas));
    } catch (captureError) {
      await this.rollbackTransaction(face, before, captureError);
      throw captureError;
    }

    try {
      this.commitSnapshots(face, before, after, canvas);
    } catch (error) {
      await this.rollbackTransaction(face, before, error);
      throw error;
    }
  };

  public commitCapturedTransaction = async (
    face: HistoryFaceIdentity,
    canvas: object,
    beforeSnapshot: HistorySnapshot,
  ): Promise<void> => {
    this.assertActive();
    if (this.isRestoring) {
      return;
    }

    const before = cloneSnapshot(beforeSnapshot);
    let after: HistorySnapshot;
    try {
      after = cloneSnapshot(this.dependencies.captureSnapshot(canvas));
      this.commitSnapshots(face, before, after, canvas);
    } catch (error) {
      await this.rollbackTransaction(face, before, error);
      throw error;
    }
  };

  public undo = (): Promise<boolean> => this.runHistoryAction('undo');

  public redo = (): Promise<boolean> => this.runHistoryAction('redo');

  public dispose = (): void => {
    this.disposed = true;
    this.undoStack = [];
    this.redoStack = [];
    this.faceRestorers.clear();
    this.pendingTransactions.clear();
    this.listeners.clear();
  };

  private livePendingTransactions = (): PendingRegistration[] =>
    Array.from(this.pendingTransactions.values()).filter(
      ({ face, canvas }) => this.dependencies.getCanvas(face) === canvas && this.dependencies.isFaceReady(face),
    );

  private hasPendingChanges = (): boolean =>
    this.livePendingTransactions().some(
      ({ participant }) => participant.hasPendingChanges() && participant.getBeforeSnapshot() !== null,
    );

  private isComposing = (): boolean =>
    this.livePendingTransactions().some(({ participant }) => participant.isComposing?.() === true);

  private rebasePendingTransactions = (
    face: HistoryFaceIdentity,
    canvas: object,
    snapshot: HistorySnapshot,
    expectedBaseline?: HistorySnapshot,
  ): void => {
    const identityKey = this.getIdentityKey(face);
    this.pendingTransactions.forEach((registration) => {
      const baseline = registration.participant.getBeforeSnapshot();
      if (
        registration.canvas === canvas &&
        this.getIdentityKey(registration.face) === identityKey &&
        (expectedBaseline === undefined || (baseline !== null && snapshotsEqual(baseline, expectedBaseline)))
      ) {
        registration.participant.rebase(cloneSnapshot(snapshot));
      }
    });
  };

  private flushPendingTransactions = (baseline?: { canvas: object; snapshot: HistorySnapshot }): void => {
    const capturedCanvases = new Set<object>();
    const transactions = this.livePendingTransactions().flatMap(({ face, canvas, participant }) => {
      const before = participant.getBeforeSnapshot();
      if (!participant.hasPendingChanges() || !before || capturedCanvases.has(canvas)) {
        return [];
      }
      capturedCanvases.add(canvas);
      const after = cloneSnapshot(
        baseline?.canvas === canvas ? baseline.snapshot : this.dependencies.captureSnapshot(canvas),
      );
      const detachedBefore = cloneSnapshot(before);
      if (
        !snapshotsEqual(detachedBefore, after) &&
        getSerializedByteLength({ face, before: detachedBefore, after }) > this.maxSerializedBytes
      ) {
        throw new Error('Edit exceeds the configured history size limit');
      }
      return [{ face, canvas, before: detachedBefore, after }];
    });
    transactions.forEach(({ face, canvas, before, after }) => this.commitSnapshots(face, before, after, canvas));
  };

  private runHistoryAction = async (direction: 'undo' | 'redo'): Promise<boolean> => {
    if (this.disposed || this.isRestoring || this.isPreparing || this.isComposing()) {
      return false;
    }
    this.isPreparing = true;
    try {
      this.flushPendingTransactions();
      const source = direction === 'undo' ? this.undoStack : this.redoStack;
      const destination = direction === 'undo' ? this.redoStack : this.undoStack;
      const entry = source[source.length - 1];
      return entry
        ? await this.restoreEntry(entry, direction === 'undo' ? entry.before : entry.after, source, destination)
        : false;
    } catch (error) {
      this.lastRestoreError = this.toError(error);
      return false;
    } finally {
      this.isPreparing = false;
      this.emitChange();
    }
  };

  private restoreEntry = async (
    entry: HistoryEntry,
    target: HistorySnapshot,
    sourceStack: HistoryEntry[],
    destinationStack: HistoryEntry[],
  ): Promise<boolean> => {
    const canvas = this.dependencies.getCanvas(entry.face);
    if (!canvas || !this.dependencies.isFaceReady(entry.face)) {
      return false;
    }

    let recoverySnapshot: HistorySnapshot;
    try {
      recoverySnapshot = cloneSnapshot(this.dependencies.captureSnapshot(canvas));
    } catch (error) {
      this.lastRestoreError = this.toError(error);
      this.emitChange();
      return false;
    }

    this.isRestoring = true;
    this.lastRestoreError = null;
    this.dependencies.setFaceReadiness(entry.face, 'loading');
    this.emitChange();

    try {
      await this.dependencies.navigateToFace(entry.face);
      await this.restoreFaceSnapshot(entry.face, target);
      this.rebasePendingTransactions(entry.face, canvas, target);
      sourceStack.pop();
      destinationStack.push(entry);
      this.dependencies.setFaceReadiness(entry.face, 'ready');
      this.lastRestoreError = null;
      return true;
    } catch (restoreError) {
      this.lastRestoreError = this.toError(restoreError);
      this.dependencies.setFaceReadiness(entry.face, 'recovery');
      try {
        await this.restoreFaceSnapshot(entry.face, recoverySnapshot);
        this.rebasePendingTransactions(entry.face, canvas, recoverySnapshot);
        this.dependencies.setFaceReadiness(entry.face, 'ready');
      } catch (recoveryError) {
        const fatalError = this.toError(recoveryError);
        this.lastRestoreError = fatalError;
        this.dependencies.markFaceFatal(entry.face, fatalError);
      }
      return false;
    } finally {
      this.isRestoring = false;
      this.emitChange();
    }
  };

  private rollbackTransaction = async (
    face: HistoryFaceIdentity,
    snapshot: HistorySnapshot,
    originalError: unknown,
  ): Promise<void> => {
    try {
      await this.restoreFaceSnapshot(face, snapshot);
    } catch (rollbackError) {
      this.dependencies.markFaceFatal(face, this.toError(rollbackError));
      throw originalError;
    }
  };

  private trimHistory = (): void => {
    while (this.getEntryCount() > this.maxEntries || this.getSerializedBytes() > this.maxSerializedBytes) {
      const oldestUndo = this.undoStack[0];
      const oldestRedo = this.redoStack[0];
      if (!oldestRedo || (oldestUndo && oldestUndo.id < oldestRedo.id)) {
        this.undoStack.shift();
      } else {
        this.redoStack.shift();
      }
    }
  };

  private commitSnapshots = (
    face: HistoryFaceIdentity,
    before: HistorySnapshot,
    after: HistorySnapshot,
    canvas: object,
  ): void => {
    if (snapshotsEqual(before, after)) {
      this.rebasePendingTransactions(face, canvas, after, before);
      this.emitChange();
      return;
    }

    const entry: HistoryEntry = {
      id: this.nextEntryId,
      face,
      before,
      after,
      serializedBytes: getSerializedByteLength({ face, before, after }),
    };
    if (entry.serializedBytes > this.maxSerializedBytes) {
      throw new Error('Edit exceeds the configured history size limit');
    }

    this.nextEntryId += 1;
    this.redoStack = [];
    this.undoStack.push(entry);
    this.trimHistory();
    this.rebasePendingTransactions(face, canvas, after);
    this.emitChange();
  };

  private getEntryCount = (): number => this.undoStack.length + this.redoStack.length;

  private isEntryUsable = (entry: HistoryEntry | undefined): boolean =>
    Boolean(entry && this.dependencies.isFaceReady(entry.face));

  private getSerializedBytes = (): number =>
    [...this.undoStack, ...this.redoStack].reduce((total, entry) => total + entry.serializedBytes, 0);

  private assertActive = (): void => {
    if (this.disposed) {
      throw new Error('History session has been disposed');
    }
  };

  private restoreFaceSnapshot = async (face: HistoryFaceIdentity, snapshot: HistorySnapshot): Promise<void> => {
    const registeredRestorer = this.faceRestorers.get(this.getIdentityKey(face));
    if (registeredRestorer) {
      await registeredRestorer(snapshot);
      return;
    }
    await this.dependencies.restoreSnapshot(face, snapshot);
  };

  private getIdentityKey = (face: HistoryFaceIdentity): string =>
    JSON.stringify([face.editorSessionKey, face.faceId, face.faceNumber, face.type]);

  private toError = (error: unknown): Error => (error instanceof Error ? error : new Error(String(error)));

  private emitChange = (): void => {
    this.listeners.forEach((listener) => listener());
  };
}
